import { StyleSheet, css } from 'aphrodite'
import { useState } from 'react'
import { userService } from '../../services/userService'
import type { ContactCreate } from '../../types/user'
import DashboardAddModal from '../dashboard/DashboardAddModal'

interface AddContactModalProps {
  onClose: () => void
  onSuccess: () => void
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Email is required')
      return
    }

    setIsSubmitting(true)
    try {
      const contactData: ContactCreate = {
        email: email.trim(),
        stakeholder_types: [],
      }
      console.log('Sending contact data:', contactData)
      await userService.addContact(contactData)
      onSuccess()
      onClose()
    } catch (err: unknown) {
      type AxiosErrorLike = {
        message?: string
        response?: {
          status?: number
          data?: unknown & { detail?: string; message?: string }
        }
      }
      const axiosErr = err as AxiosErrorLike
      const status = axiosErr?.response?.status
      const serverData = axiosErr?.response?.data as
        | { detail?: string; message?: string }
        | undefined
      let message = 'Failed to add contact'
      if (status === 404) {
        message = 'User not found'
      } else if (status === 400 && serverData) {
        if (typeof serverData?.detail === 'string') message = serverData.detail
        else if (typeof serverData?.message === 'string')
          message = serverData.message
        else message = 'Invalid request. Please check the email and try again.'
      } else if (
        typeof axiosErr?.message === 'string' &&
        axiosErr.message.includes('Network')
      ) {
        message = 'Network error. Please check your connection and try again.'
      }
      setError(message)
      console.error('Failed to add contact', {
        status,
        serverData,
        error: axiosErr,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DashboardAddModal
      title="Add Contact"
      onClose={onClose}
      onSubmit={handleSubmit}
      step={1}
      totalSteps={1}
      isSubmitting={isSubmitting}
      skipCancelConfirmation
      skipSendConfirmation
    >
      <div className={css(styles.formContainer)}>
        {error && <div className={css(styles.error)}>{error}</div>}
        <div className={css(styles.formGroup)}>
          <label className={css(styles.label)}>Email</label>
          <input
            type="email"
            className={css(styles.input)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address"
          />
        </div>
      </div>
    </DashboardAddModal>
  )
}

const styles = StyleSheet.create({
  formContainer: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  error: {
    color: '#EF4444',
    marginBottom: '16px',
    padding: '8px 12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '4px',
    fontSize: '14px',
  },
})
